import { useEffect } from "react";

/**
 * DashboardTour — first-visit driver.js walkthrough.
 * Loads driver.js lazily, only runs when the user hasn't seen it before and
 * we're inside /dashboard root. Brutalist styles are in index.css.
 */
const SEEN_KEY = "jh_tour_seen_v1";

export default function DashboardTour({ force = false }) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!force) {
      try {
        if (window.localStorage.getItem(SEEN_KEY) === "1") return;
      } catch {
        return;
      }
    }

    let cancelled = false;
    (async () => {
      try {
        // dynamic import keeps driver.js out of the initial bundle
        const mod = await import("driver.js");
        await import("driver.js/dist/driver.css");
        if (cancelled) return;
        const driver = mod.driver || mod.default;

        // 18-step V2 walkthrough — touches every dashboard surface.
        // Steps with elements that aren't currently mounted (e.g. tile-* off
        // the Home grid) are skipped by driver.js automatically.
        const steps = [
          {
            element: '[data-testid="dash-welcome"]',
            popover: {
              title: "Welcome to JobHacki V2",
              description:
                `Every tool is REAL — no 'coming soon'. Here's a 60-second tour.<br/><br/>
                 <iframe src="https://www.youtube.com/embed/aqz-KE-bpKQ?rel=0&modestbranding=1"
                         width="100%" height="180" frameborder="0" allowfullscreen
                         style="border:2px solid #0a0a0a;background:#000"></iframe>`,
              side: "bottom",
              align: "start",
            },
          },
          {
            element: '[data-testid="dash-version-switcher"]',
            popover: {
              title: "V2/V1 switcher",
              description:
                "Click 'DASHBOARD ▾' to flip between the new V2 and the classic V1 dashboard at any time.",
              side: "bottom",
            },
          },
          {
            element: '[data-testid="tile-jobs"]',
            popover: {
              title: "Job Directory",
              description: "Live ATS jobs (300k+), filterable by category, level, location, ATS, recency.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-playbooks"]',
            popover: {
              title: "Playbook Directory",
              description:
                "Grounded money + career playbooks. Free ones open in a new tab; PREMIUM unlocks with All-Access.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-toolbox"]',
            popover: {
              title: "Toolbox",
              description: "Curated tools, prompts, repos — cross-linked to the playbooks that use them.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-match"]',
            popover: {
              title: "Resume Match",
              description:
                "Upload your resume (PDF/Word/text) — we score it 0–100 against live ATS roles in seconds.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-readiness"]',
            popover: {
              title: "Job Readiness",
              description: "Paste a real posting — get a tailored readiness score + study plan.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-simulator"]',
            popover: {
              title: "Readiness Simulator",
              description: "Voice + text role-play. Practice the interview, get scored on the spot.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-rolefit"]',
            popover: {
              title: "RoleFit",
              description: "Take the assessment for any role — 40 categories, score syncs to your dashboard.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-career-path"]',
            popover: {
              title: "Career Path",
              description: "Progression view — see what the next role looks like and what's missing.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-resume"]',
            popover: {
              title: "Resume Builder",
              description: "Build/edit your resume — templates, fonts, colors. Save, reopen, export PDF.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-saved"]',
            popover: {
              title: "Saved Jobs",
              description: "Bookmark roles you like from the directory — they live here cross-device.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-alerts"]',
            popover: {
              title: "Job Alerts",
              description: "Tap the bell on any job to get notified when the listing changes.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-applications"]',
            popover: {
              title: "Applications",
              description: "Every job you hit Apply on lands here — confirm + track your funnel.",
              side: "right",
            },
          },
          {
            element: '[data-testid="tile-omnicut"]',
            popover: {
              title: "OmniCut",
              description: "Long-form → short clips, hooks, captions. Paid; opens in a new tab.",
              side: "left",
            },
          },
          {
            element: '[data-testid="tile-omniscript"]',
            popover: {
              title: "OmniScript",
              description: "Hook-first AI scripts. Use it after OmniCut for end-to-end content velocity.",
              side: "left",
            },
          },
          {
            element: '[data-testid="topbar-bell"]',
            popover: {
              title: "Notifications",
              description: "Saved jobs, alerts, application updates all land here. Mark all read with one click.",
              side: "bottom",
              align: "end",
            },
          },
          {
            element: '[data-testid="dash-topbar-unlock"], [data-testid="dash-topbar-pro"]',
            popover: {
              title: "Free vs ALL-ACCESS",
              description:
                "Free tools work out of the box. Unlock the paid set (Resume Builder, Simulator, Career Path…) one-time, no subscription. You can replay this tour anytime via the 'Replay Tour' button in the sidebar.",
              side: "left",
            },
          },
        ];

        const d = driver({
          showProgress: true,
          allowClose: true,
          allowKeyboardControl: true,
          smoothScroll: true,
          steps,
          popoverClass: "jh-tour-popover",
          onDestroyed: () => {
            try {
              window.localStorage.setItem(SEEN_KEY, "1");
            } catch {
              /* ignore */
            }
          },
        });

        // Wait for tiles to render
        setTimeout(() => {
          if (!cancelled) d.drive();
        }, 500);
      } catch {
        // driver.js failed to load — silently skip; non-blocking UX.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [force]);

  return null;
}

export function restartTour() {
  try {
    window.localStorage.removeItem(SEEN_KEY);
    window.location.reload();
  } catch {
    /* ignore */
  }
}
